import { ReactNode, useMemo, useState } from 'react';
import {
  Control,
  FieldArrayWithId,
  Path,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import PlusIcon from '@/assets/icons/plus.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useTranslation } from '@/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import StyledInput from '../styled/StyledInput';
import StyledDatePicker from '../styled/StyledDatePicker';
import StyledCheckboxGroup from '../styled/StyledCheckboxGroup';
import StyledButton from '../styled/StyledButton';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { WEEKDAYS, WEEKDAYS_TRANSLATED } from '@/constants/weekdays';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { HOTEL_PENSION_TYPE_TRANSLATION_KEYS } from '@/constants/hotelPension';
import { getPensionTypesAbove } from '@/utils/estimates/hotel/computeHotelPensionPrice';
import { buildEmptyPensionCard } from '@/utils/benefits/hotel/buildEmptyPensionCard';
import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import type {
  HotelPensionPricingFormData,
  HotelPensionPricingPeriodFormData,
  HotelPensionPriceFormData,
} from '@/types/benefits/hotel/HotelPensionPricingSchema';
import { HotelPensionPricingSchema } from '@/types/benefits/hotel/HotelPensionPricingSchema';
import clsx from 'clsx';
import PensionTariffPaste from './excelPaste/PensionTariffPaste';
import PensionTariffsPaste from './excelPaste/PensionTariffsPaste';
import { useExpertMode } from './excelPaste/useExpertMode';
import WithExcelPastePanel from './excelPaste/WithExcelPastePanel';
import { PASTE_HOST_PROPS } from './excelPaste/expertLayout';
import { toChildBandsCount } from './excelPaste/pasteFormHelpers';

type PensionPricingsFieldPath =
  | 'commonPensionPricings'
  | `hotelRoomCategories.${number}.categoryPensionPricings`;

type HotelPensionPricingFieldWithId = FieldArrayWithId<
  HotelFormDataWithTranslations,
  PensionPricingsFieldPath
>;

interface HotelOtherPensionTypesPricesProps {
  control: Control<HotelFormDataWithTranslations>;
  isViewMode?: boolean;
  categoryIndex?: number;
}

const HotelOtherPensionTypesPrices = ({
  control,
  isViewMode = false,
  categoryIndex,
}: HotelOtherPensionTypesPricesProps) => {
  const { t } = useTranslation();
  const isExpertMode = useExpertMode() && !isViewMode;

  const fieldArrayName: PensionPricingsFieldPath =
    categoryIndex != null
      ? `hotelRoomCategories.${categoryIndex}.categoryPensionPricings`
      : 'commonPensionPricings';

  const { fields, append, remove, replace } = useFieldArray<
    HotelFormDataWithTranslations,
    PensionPricingsFieldPath
  >({
    control,
    name: fieldArrayName,
  });

  const pricingPensionType = useWatch({ control, name: 'pricingPensionType' });
  const childAges = useWatch({ control, name: 'childAges' });
  const acceptsChildPricing = useWatch({
    control,
    name: 'acceptsChildPricing',
  });

  const numberOfChildDistributions =
    acceptsChildPricing === 'true' ? (childAges?.length ?? 0) : 0;

  const pensionPricingsValues = useWatch({
    control,
    name: fieldArrayName,
  }) as HotelFormDataWithTranslations['commonPensionPricings'] | undefined;

  const relevantPensionTypes = useMemo(() => {
    const label = pricingPensionType?.value;
    return getPensionTypesAbove(label as string | undefined);
  }, [pricingPensionType]);

  const handleAddCard = () => append(buildEmptyPensionCard());

  const handleDuplicate = (index: number) => {
    const current: HotelPensionPricingFieldWithId = fields[index];
    const copy = {
      name: `${current.name || t('tariffs.tarif_', { index: index + 1 })} (${t('common.copy')})`,
      periods: (current.periods ?? []).map(
        (p: HotelPensionPricingPeriodFormData & { id?: string }) => ({
          ...p,
          id: undefined,
        }),
      ),
      prices: (current.prices ?? []).map(
        (pr: HotelPensionPriceFormData & { id?: string }) => ({
          ...pr,
          id: undefined,
        }),
      ),
    };
    append(copy);
  };

  return (
    <div {...PASTE_HOST_PROPS} className="flex flex-col gap-4">
      {fields.map((field, cardIndex) => (
        <PensionPricingCard
          key={field.id}
          control={control}
          fieldArrayName={fieldArrayName}
          cardIndex={cardIndex}
          pensionData={pensionPricingsValues?.[cardIndex]}
          isViewMode={isViewMode}
          relevantPensionTypes={relevantPensionTypes}
          numberOfChildDistributions={numberOfChildDistributions}
          childAges={childAges}
          onDelete={() => remove(cardIndex)}
          onDuplicate={() => handleDuplicate(cardIndex)}
          pastePanel={
            isExpertMode ? (
              <div className="flex flex-col gap-4">
                {/* Every pension tariff at once, above the first tariff's. */}
                {cardIndex === 0 && (
                  <PensionTariffsPaste
                    fieldArrayName={fieldArrayName}
                    childBandsCount={toChildBandsCount(
                      numberOfChildDistributions,
                    )}
                    childAges={childAges}
                    replaceCards={replace}
                  />
                )}
                <PensionTariffPaste
                  fieldArrayName={fieldArrayName}
                  cardIndex={cardIndex}
                  childBandsCount={toChildBandsCount(
                    numberOfChildDistributions,
                  )}
                  childAges={childAges}
                />
              </div>
            ) : null
          }
        />
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('tariffs.addTarif')}
          onClick={handleAddCard}
          type="button"
          variant="outlined"
          className="text-brand-600 w-fit"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </div>
  );
};

interface PensionPricingCardProps {
  control: Control<HotelFormDataWithTranslations>;
  fieldArrayName: PensionPricingsFieldPath;
  cardIndex: number;
  pensionData?: HotelPensionPricingFormData;
  isViewMode: boolean;
  relevantPensionTypes: HotelPensionType[];
  numberOfChildDistributions: number;
  childAges:
    | Array<{
        childAgeFrom?: number;
        childAgeTo?: number;
      }>
    | undefined;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Expert mode paste zone, shown next to the card's fields. */
  pastePanel?: ReactNode;
}

function PensionPricingCard({
  control,
  fieldArrayName,
  cardIndex,
  pensionData,
  isViewMode,
  relevantPensionTypes,
  numberOfChildDistributions,
  childAges,
  onDelete,
  onDuplicate,
  pastePanel,
}: PensionPricingCardProps) {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const [isFolded, setIsFolded] = useState(false);

  const parseResult = HotelPensionPricingSchema.safeParse(pensionData);
  const validationError =
    parseResult.success === false ? parseResult.error : undefined;

  const cardPrefix = `${fieldArrayName}.${cardIndex}` as const;
  const periodsFieldName = `${cardPrefix}.periods` as const;

  const {
    fields: periodFields,
    append: appendPeriod,
    remove: removePeriod,
  } = useFieldArray<HotelFormDataWithTranslations, typeof periodsFieldName>({
    control,
    name: periodsFieldName,
  });

  const firstPeriodApplicationDays = useWatch({
    control,
    name: `${periodsFieldName}.0.applicationDays` as Path<HotelFormDataWithTranslations>,
  }) as string[] | undefined;

  const currentTitle = useWatch({
    control,
    name: `${cardPrefix}.name` as Path<HotelFormDataWithTranslations>,
  });

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className="text-base leading-6 hover:cursor-pointer"
          onClick={() => setIsFolded(!isFolded)}
        >
          {(currentTitle as string) ||
            t('tariffs.tarif_', { index: cardIndex + 1 })}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {!isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.duplicate')}>
                <CopyIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={() => {
                    setIsFolded(true);
                    onDuplicate();
                  }}
                />
              </StyledTooltipContainer>
            )}
            {cardIndex > 0 && !isViewMode && (
              <StyledTooltipContainer tooltipMessage={t('common.delete')}>
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={onDelete}
                />
              </StyledTooltipContainer>
            )}
          </div>
          {isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          )}
        </div>
      </CardHeader>
      {!isFolded && (
        <CardContent>
          <WithExcelPastePanel panel={pastePanel} isViewMode={isViewMode}>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col">
                {validationError && !isViewMode && (
                  <>
                    {validationError.issues
                      .filter(({ code }) => code === 'custom')
                      .map(({ message }, indexError) => (
                        <p
                          className="text-sm leading-6 font-medium text-error-500"
                          key={`error-${indexError}`}
                        >
                          {message}
                        </p>
                      ))}
                  </>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <StyledInput
                  control={control}
                  name={
                    `${cardPrefix}.name` as Path<HotelFormDataWithTranslations>
                  }
                  placeholder={t('tariffs.placeholder')}
                  disabled={isViewMode}
                />
                {/* Periods block: label + dates + application days only (like NestedTarifPeriods) */}
                {periodFields.map((periodField, periodIndex) => (
                  <div className="mb-1" key={periodField.id}>
                    <div
                      className={clsx('flex flex-col', {
                        'w-full gap-1.5': !isViewMode,
                        'lg:flex-row md:justify-between gap-6': isViewMode,
                      })}
                    >
                      <div
                        className={clsx({ 'flex flex-col w-full': isViewMode })}
                      >
                        <div
                          className={clsx({
                            'flex justify-between': !isViewMode,
                          })}
                        >
                          <Label className="text-gray-700">
                            {t('tariffs.period_', {
                              index: periodIndex + 1,
                              tarifIndex: cardIndex + 1,
                            })}
                            {' *'}
                          </Label>
                          {periodFields.length > 1 && !isViewMode && (
                            <StyledButton
                              title={t('common.delete')}
                              className="p-0 h-auto"
                              variant="link"
                              onClick={() => removePeriod(periodIndex)}
                            />
                          )}
                        </div>
                        <div
                          className={clsx('flex items-center gap-2', {
                            'flex-col md:flex-row w-full': !isViewMode,
                          })}
                        >
                          <span>{t('common.from')}</span>
                          <StyledDatePicker
                            control={control}
                            name={`${periodsFieldName}.${periodIndex}.startDate`}
                            className="w-full"
                            isViewMode={isViewMode}
                            enforceCurrentDateBoundary={false}
                          />
                          <span>{t('common.to')}</span>
                          <StyledDatePicker
                            control={control}
                            name={`${periodsFieldName}.${periodIndex}.endDate`}
                            className="w-full"
                            isViewMode={isViewMode}
                            enforceCurrentDateBoundary={false}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col w-full gap-1.5">
                        <Label className="text-gray-700">
                          {t('tariffs.validDays')} *
                        </Label>
                        <StyledCheckboxGroup
                          control={control}
                          name={`${periodsFieldName}.${periodIndex}.applicationDays`}
                          options={WEEKDAYS.map((weekday) => ({
                            value: weekday,
                            label:
                              getTranslatedConst(WEEKDAYS_TRANSLATED)[
                                weekday
                              ][0],
                          }))}
                          isViewMode={isViewMode}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {!isViewMode && (
                  <StyledButton
                    className="text-brand-600 w-fit pl-0"
                    title={t('tariffs.addPeriod')}
                    onClick={() => {
                      appendPeriod({
                        startDate: undefined,
                        endDate: undefined,
                        applicationDays: firstPeriodApplicationDays ?? [],
                      });
                    }}
                    variant="link"
                    icon={<PlusIcon className="size-5 fill-brand-600" />}
                  />
                )}
                {/* One prices table per card (card-level prices) */}
                <PensionPricesTable
                  control={control}
                  pricesPrefix={`${cardPrefix}.prices`}
                  relevantPensionTypes={relevantPensionTypes}
                  numberOfChildDistributions={numberOfChildDistributions}
                  childAges={childAges}
                  isViewMode={isViewMode}
                />
              </div>
            </div>
          </WithExcelPastePanel>
        </CardContent>
      )}
    </Card>
  );
}

interface PensionPricesTableProps {
  control: Control<HotelFormDataWithTranslations>;
  pricesPrefix: string;
  relevantPensionTypes: HotelPensionType[];
  numberOfChildDistributions: number;
  childAges:
    | Array<{
        childAgeFrom?: number;
        childAgeTo?: number;
      }>
    | undefined;
  isViewMode: boolean;
}

function PensionPricesTable({
  control,
  pricesPrefix,
  relevantPensionTypes,
  numberOfChildDistributions,
  childAges,
  isViewMode,
}: PensionPricesTableProps) {
  const { t } = useTranslation();

  const allPrices = useWatch({
    control,
    name: pricesPrefix as Path<HotelFormDataWithTranslations>,
  }) as Array<{ pensionType?: string; [key: string]: unknown }> | undefined;

  const priceIndices = useMemo(() => {
    if (!allPrices) return {};
    const map: Record<string, number> = {};
    allPrices.forEach((p, idx) => {
      if (p?.pensionType) map[p.pensionType] = idx;
    });
    return map;
  }, [allPrices]);

  if (relevantPensionTypes.length === 0) return null;

  return (
    <table className="w-fit">
      <thead>
        <tr>
          <th className="text-left p-2">
            <Label className="text-gray-700">{t('hotels.pensionPrice')}</Label>
          </th>
          <th className="text-left p-2">
            <Label className="text-gray-700">{t('hotels.adult')}</Label>
          </th>
          {numberOfChildDistributions > 1 && childAges && (
            <th className="text-left p-2">
              <Label className="text-gray-700">
                {t('hotels.childAge', {
                  ageFrom: childAges[1].childAgeFrom,
                  ageTo: childAges[1].childAgeTo,
                })}
              </Label>
            </th>
          )}
          {numberOfChildDistributions > 0 && childAges && (
            <th className="text-left p-2">
              <Label className="text-gray-700">
                {t('hotels.childAge', {
                  ageFrom: childAges[0].childAgeFrom,
                  ageTo: childAges[0].childAgeTo,
                })}
              </Label>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {relevantPensionTypes.map((pensionType) => {
          const priceIdx = priceIndices[pensionType];
          if (priceIdx == null) return null;

          return (
            <tr key={pensionType}>
              <td className="p-2">
                <Label className="text-gray-700">
                  {t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS[pensionType])}
                </Label>
              </td>
              <td className="p-2 w-24">
                <StyledInput
                  control={control}
                  name={
                    `${pricesPrefix}.${priceIdx}.adultPrice` as Path<HotelFormDataWithTranslations>
                  }
                  type="number"
                  min={0}
                  disabled={isViewMode}
                  hideNumberFieldArrows
                />
              </td>
              {numberOfChildDistributions > 1 && childAges && (
                <td className="p-2 w-24">
                  <StyledInput
                    control={control}
                    name={
                      `${pricesPrefix}.${priceIdx}.adolescentPrice` as Path<HotelFormDataWithTranslations>
                    }
                    type="number"
                    min={0}
                    disabled={isViewMode}
                    hideNumberFieldArrows
                  />
                </td>
              )}
              {numberOfChildDistributions > 0 && childAges && (
                <td className="p-2 w-24">
                  <StyledInput
                    control={control}
                    name={
                      `${pricesPrefix}.${priceIdx}.childPrice` as Path<HotelFormDataWithTranslations>
                    }
                    type="number"
                    min={0}
                    disabled={isViewMode}
                    hideNumberFieldArrows
                  />
                </td>
              )}
              <td className="p-2">
                <Label className="text-gray-700">{t('hotels.by_person')}</Label>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default HotelOtherPensionTypesPrices;
